import "./CustomChessboard.scss";
import { CustomBoardConstants } from "../../../utils/constants";
import {
    calcBoardPadding,
    findKingFromFenString,
    useResponsiveBoardWidth,
} from "../../../utils/utils";
import { useCustomPieces } from "../CustomPieces/CustomPieceHook";
import { useGame } from "../../../context/GameContext";
import { useEffect, useState, useRef } from "react";
import { Chessboard, ChessboardDnDProvider } from "react-chessboard";
import { KeyCodes, toggleInGameMenu } from "../../../utils/navigationHelpers";
// There are two different piece types. One from react-chessboard and one from chess.js. Cmd-click to see the difference.
import { Piece as ReactChessboardPiece } from "react-chessboard/dist/chessboard/types";
import { Chess, Color, Move, Square } from "chess.js";
import { handleBoardNavigation } from "./boardNavigation";
import { useMultiplayer } from "../../../context/MultiplayerContext";
import useMoveHandler from "../../../hooks/useMoveHandler";
import { getAIMove } from "../../../utils/computer";
import { useDevice } from "../../../context/DeviceContext";
import PuzzleIncorrectMove from "../../Menus/PuzzleIncorrectMove/PuzzleIncorrectMove";
import PuzzleComplete from "../../Menus/PuzzleComplete/PuzzleComplete";
import { savePuzzleAttempt, getTodayDateString } from "../../../utils/puzzleHistoryService";
import { handlePuzzleMove } from "../../../utils/puzzleMoveHandler";
/**
 * The CustomChessboard component is a wrapper around the react-chessboard component.
 * It provides additional functionality such as
 * highlighting available moves, drag-and-drop, and promotion.
 * @returns the CustomChessboard component
 */
export default function CustomChessboard() {
    const [availableMoves, setAvailableMoves] = useState<Square[]>([]); // The available moves for the clicked piece.
    const [clickedSquare, setClickedSquare] = useState<Square | null>(null); // The piece that was clicked
    const [hoveredSquare, setHoveredSquare] = useState<Square | null>(null); // The square that is being hovered over
    const [isIndicatorVisble, setIsIndicatorVisble] = useState(true);
    const [pendingMove, setPendingMove] = useState<Move | null>(null); // Stores the pending move details
    const playerTurn = useRef(true); // Track if players turn
    const { isMobile } = useDevice();
    const [showIncorrectMoveModal, setShowIncorrectMoveModal] = useState(false);
    const [incorrectMoveSquare, setIncorrectMoveSquare] = useState<Square | null>(null);
    const puzzleStateBeforeMove = useRef<{ fen: string; index: number } | null>(null);
    const [showPuzzleCompleteModal, setShowPuzzleCompleteModal] = useState(false);

    const {
        gameState,
        setGameState,
        promotion,
        setPromotion,
        remoteNavigation,
        setRemoteNavigation,
    } = useGame();

    const { handleMove, getMove, allowMove } = useMoveHandler();
    const { multiplayerState, setMultiplayerState, sendMultiplayerMove } = useMultiplayer();

    /**
     * Update playerTurn ref when gameState changes and make AI move appropriately.
     * This only runs when the AI is playing.
     */
    useEffect(() => {
        if (!gameState.game) return;
        if (gameState.game.isGameOver()) return;

        const currentTurn = gameState.game.turn();
        if (currentTurn === "w") {
            playerTurn.current = true;
            return;
        }

        // Opponents turn
        playerTurn.current = false;
        if (!gameState.isAIGame) return;

        (async () => {
            const move = await getAIMove(gameState.game!.fen(), gameState.aiDifficulty);
            if (!move) return;
            setPendingMove(move);
        })();
    }, [gameState, setPendingMove]);

    /**
     * Executes pending moves when they are ready.
     */
    useEffect(() => {
        if (!pendingMove) return;
        if (promotion.open) return;
        if (pendingMove.color !== gameState.game?.turn()) return;

        setPendingMove(null);
        resetSelection();
        if (promotion.type === "cancel") return;

        if (!gameState.isAIGame) {
            const move = new Chess(gameState.game?.fen()).move({
                from: pendingMove.from,
                to: pendingMove.to,
                promotion: promotion.type ?? undefined,
            });
            setGameState((prev) => ({
                ...prev,
                game: new Chess(move.after),
                lastMove: move,
            }));
            sendMultiplayerMove(move);
            return;
        }
        handleMove(pendingMove);
    }, [
        pendingMove,
        promotion.open,
        setPendingMove,
        gameState.isAIGame,
        sendMultiplayerMove,
        gameState.game,
        handleMove,
        promotion.type,
        setGameState,
    ]);

    useEffect(() => {
        let timeout: NodeJS.Timeout;

        const resetTimer = () => {
            setIsIndicatorVisble(true);
            clearTimeout(timeout);
            timeout = setTimeout(() => {
                setIsIndicatorVisble(false);
            }, 3000); // 3 seconds delay
        };
        const handleInput = () => resetTimer();

        window.addEventListener("keydown", handleInput);

        resetTimer();

        return () => {
            clearTimeout(timeout);
            window.removeEventListener("keydown", handleInput);
        };
    }, []);

    /**
     * When the game is over (checkmate, stalemate, etc.), activate the Game Over menu
     * and set the initial focus index to the first option (e.g., "Play Again").
     */
    useEffect(() => {
        if (!gameState.game) return;
        const isNewGame = gameState.game.history().length === 0;

        if (isNewGame) {
            setClickedSquare(null);
            setAvailableMoves([]);
            setHoveredSquare(null);
            setPendingMove(null);
            setRemoteNavigation((prev) => ({
                ...prev,
                isInGameOverMenu: false,
                selectedGameOverIcon: null,
                remoteClickSquare: null,
                remoteOverSquare: null,
            }));
            return;
        }

        if (gameState.game.isGameOver()) {
            setRemoteNavigation((prev) => ({
                ...prev,
                isInGameOverMenu: true,
                selectedGameOverIcon: "Play Again",
            }));
            return;
        }
    }, [
        gameState.game,
        setRemoteNavigation,
        setClickedSquare,
        setAvailableMoves,
        setHoveredSquare,
        setPendingMove,
    ]);

    /**
     * Save puzzle completion to localStorage when puzzle is solved
     */
    useEffect(() => {
        if (showPuzzleCompleteModal && gameState.isPuzzle) {
            // Use the puzzle date if available, otherwise use today
            const puzzleDate = gameState.puzzleDate || getTodayDateString();
            const hintsUsed = gameState.puzzleHintLevel || 0;

            savePuzzleAttempt(
                puzzleDate,
                {
                    title: "Daily Puzzle",
                    fen: gameState.game?.fen() || "",
                    pgn: "",
                    moves: gameState.puzzleSolution || [],
                    playerColor: multiplayerState.playerColor,
                },
                true,
                hintsUsed,
                false, // Don't increment - already counted when they started
            );
        }
    }, [
        showPuzzleCompleteModal,
        gameState.isPuzzle,
        gameState.puzzleHintLevel,
        gameState.puzzleSolution,
        gameState.game,
        gameState.puzzleDate,
        multiplayerState.playerColor,
    ]);

    /**
     * Automatically sets keyboard focus to the currently selected Game Over menu button
     * whenever the Game Over menu becomes active or the selected index changes.
     */
    // Focus management for GameOver menu - DISABLED since GameOver component now handles its own navigation
    // useEffect(() => {
    //     if (!remoteNavigation.isInGameOverMenu) return;

    //     // Skip focus management during review mode - handled by MenuIcons component
    //     if (gameState.isReviewMode) return;

    //     let navRow = 0;
    //     if (remoteNavigation.selectedGameOverIcon === "Play Again") {
    //         navRow = 0;
    //     } else if (remoteNavigation.selectedGameOverIcon === "Main Menu") {
    //         navRow = 1;
    //     } else if (remoteNavigation.selectedGameOverIcon === "Review Board") {
    //         navRow = 2;
    //     }

    //     const activeElement = document.querySelector<HTMLElement>(
    //         `[data-navrow="${navRow}"][data-navcol="0"]`,
    //     );
    //     activeElement?.focus();
    // }, [remoteNavigation.selectedGameOverIcon, remoteNavigation.isInGameOverMenu, gameState.isReviewMode]);

    const resetSelection = () => {
        setClickedSquare(null);
        setAvailableMoves([]);
    };

    /**
     * Handles the click event on a square.
     * Highlights available moves for the clicked piece.
     * @param square the square that was clicked.
     */
    function onSquareClick(square: Square) {
        const { game } = gameState;
        if (!game) {
            console.error("Game is null.");
            return;
        }

        if (!allowMove()) return;

        // If clicking on the same piece that's already selected, deselect it
        if (square === clickedSquare) {
            resetSelection();
            setRemoteNavigation((prev) => ({
                ...prev,
                remoteClickSquare: null,
                remoteOverSquare: square,
            }));
            return;
        }

        const gameSquare = game.get(square);

        // If clicking on an available move square, make the move
        if (clickedSquare && availableMoves.includes(square)) {
            const move = getMove(clickedSquare, square);

            if (!move) {
                resetSelection();
                console.error("No move: ", move);
                return;
            }

            // PUZZLE MODE - Handle puzzle logic for click-to-move
            if (gameState.isPuzzle) {
                handlePuzzleMove({
                    move,
                    gameState,
                    setGameState,
                    setIncorrectMoveSquare,
                    setShowIncorrectMoveModal,
                    setShowPuzzleCompleteModal,
                    resetSelection,
                    puzzleStateBeforeMove,
                });
                return;
            }

            // Normal flow (AI/Multiplayer) - non-puzzle mode
            setPendingMove(move);

            if (move.isPromotion()) {
                setPromotion({ type: null, open: true });
            }
            return;
        } else if (gameSquare && gameSquare.color === game.turn()) {
            // If a new piece is clicked, highlight its available moves
            if (gameSquare.color !== multiplayerState.playerColor) return;

            const moves = game.moves({ square, verbose: true });
            const available = moves.map((move) => move.to);
            setClickedSquare(square);
            setAvailableMoves(available);
        } else if (clickedSquare) {
            // If clicking on an empty square or enemy piece while a piece is selected, deselect
            resetSelection();
            setRemoteNavigation((prev) => ({
                ...prev,
                remoteClickSquare: null,
                remoteOverSquare: square,
            }));
        }
    }

    /**
     * Handles the drop event (drag-and-drop).
     * Shows the available moves for the piece being dragged.
     * @param sourceSquare the source square of the piece.
     * @param targetSquare the target square of the piece.
     */
    function onDrop(sourceSquare: Square, targetSquare: Square): boolean {
        if (!gameState.game) {
            console.error("Game is null.");
            return false;
        }
        if (!allowMove()) return false;

        const move = getMove(sourceSquare, targetSquare); // ✅ move exists here
        if (!move) {
            resetSelection();
            return false;
        }

        // PUZZLE MODE
        if (gameState.isPuzzle) {
            return handlePuzzleMove({
                move,
                gameState,
                setGameState,
                setIncorrectMoveSquare,
                setShowIncorrectMoveModal,
                setShowPuzzleCompleteModal,
                resetSelection,
                puzzleStateBeforeMove,
            });
        }

        // Normal flow (AI/Multiplayer)
        setPendingMove(move);
        if (move.isPromotion()) setPromotion((prev) => ({ ...prev, open: true }));
        return true;
    }

    /**
     * Handles the drag event.
     * Shows the available moves for the piece being dragged.
     */
    function onPieceDragBegin(piece: ReactChessboardPiece, square: Square) {
        if (!gameState.game) {
            console.error("Game is null.");
            return;
        }
        if (!allowMove()) return;

        const moves = gameState.game.moves({
            square,
            verbose: true,
        });
        const available = moves.map((move) => move.to);

        setClickedSquare(square);
        setAvailableMoves(available);
    }

    function handleKeyPress(event: KeyboardEvent) {
        event.preventDefault();
        const keyMapping = {
            isLeft: KeyCodes.Lefts.includes(event.keyCode),
            isRight: KeyCodes.Rights.includes(event.keyCode),
            isUp: KeyCodes.Ups.includes(event.keyCode),
            isDown: KeyCodes.Downs.includes(event.keyCode),
            isEnter: [KeyCodes.Enter, "Enter"].includes(event.keyCode),
            isEscape: [KeyCodes.Escape, "Escape"].includes(event.keyCode),
        };
        return keyMapping;
    }

    /**
     * Called whenever a user presses a key on the keyboard
     * @param event - The keyboard event object.
     */
    function handleKeyDown(event: KeyboardEvent) {
        if (multiplayerState.isHost) return;
        event.preventDefault();
        if (remoteNavigation.isInGameOverMenu) return; // Prevents chessboard navigation when game over menu is open
        if (gameState.isReviewMode) return; // Prevents chessboard navigation when in review mode
        if (promotion.open) return; // Prevents interaction when promotion menu is open
        if (gameState.showInGameMenu) return; // Prevent interaction w/ board when the in game menu is open
        if (gameState) if (!gameState.showGame) return;

        const { isLeft, isRight, isUp, isDown, isEnter, isEscape } = handleKeyPress(event);

        if (isEscape) {
            toggleInGameMenu(setGameState);
            return;
        }

        handleBoardNavigation({
            isLeft,
            isRight,
            isUp,
            isDown,
            isEnter,
            remoteNavigation,
            multiplayerState,
            gameState,
            setGameState,
            setMultiplayerState,
            setRemoteNavigation,
            onSquareClick,
            allowMove,
            setHoveredSquare,
        });
    }

    useEffect(() => {
        if (remoteNavigation.isInGameOverMenu) return;
        window.addEventListener("keydown", handleKeyDown);
        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [
        clickedSquare,
        availableMoves,
        remoteNavigation,
        promotion.open,
        gameState.showInGameMenu,
        gameState.isReviewMode,
        remoteNavigation.isInGameOverMenu,
        handleKeyDown,
    ]);

    /**
     * Apply inline style for puzzle hint square using customSquareStyles
     */
    useEffect(() => {
        // Force a re-render of customSquareStyles when hint changes
        // This is handled by the dependency in the Chessboard component
    }, [gameState.puzzleHintSquare]);

    /**
     * Apply CSS class to incorrect move square for animation (affects square only, not piece)
     */
    useEffect(() => {
        if (incorrectMoveSquare) {
            const squareElement = document.querySelector(`[data-square="${incorrectMoveSquare}"]`);
            if (squareElement) {
                squareElement.classList.add("incorrect-square");
                // Remove class after animation completes (3 pulses * 0.5s = 1.5s)
                const timeout = setTimeout(() => {
                    squareElement.classList.remove("incorrect-square");
                }, 1500);
                return () => clearTimeout(timeout);
            }
        }
    }, [incorrectMoveSquare]);

    /**
     * Highlights squares for available moves.
     * If the move is a capture, the square is highlighted in red.
     * If the game is in check, the king square is highlighted in red.
     * @returns the styles for the highlighted squares.
     */
    function customSquareStyles(isMobile: boolean) {
        if (remoteNavigation.isInGameOverMenu) {
            return {}; // disables square highlights when game over menu is open
        }
        if (gameState.isReviewMode) {
            return {}; // disables square highlights during review mode
        }

        if (!gameState.game) {
            console.error("Game is null in customSquareStyles.");
            return;
        }
        if (gameState.game.isGameOver()) return;

        // Highlight last move
        const highlightStyles: { [key: string]: React.CSSProperties } = {};
        if (gameState.lastMove && gameState.lastMove.from && gameState.lastMove.to) {
            highlightStyles[gameState.lastMove.from] = {
                background: "radial-gradient(circle, rgba(255, 255, 0, 0.5))",
                borderRadius: "50%",
            };
            highlightStyles[gameState.lastMove.to] = {
                background: "radial-gradient(circle, rgba(255, 255, 0, 0.5))",
                borderRadius: "50%",
            };
        }

        // Highlight the king square with flashing red if in check
        if (gameState.game.inCheck()) {
            const kingSquare = findKingFromFenString(gameState.game.fen(), gameState.game.turn());
            if (!kingSquare) {
                console.error("King square is null.");
                return;
            }
            highlightStyles[kingSquare] = CustomBoardConstants.KING_SQUARE_INCHECK_STYLES;
        }

        // Highlight clicked piece square
        if (clickedSquare) {
            highlightStyles[clickedSquare] = {
                background: `radial-gradient(circle, rgba(255, 255, 0, 0.5))`,
                borderRadius: "50%",
            };
        }

        // Highlight remote navigation square (disabled on mobile)
        if (
            remoteNavigation.remoteOverSquare &&
            (gameState.isAIGame || gameState.isPuzzle) &&
            isIndicatorVisble &&
            !isMobile
        ) {
            highlightStyles[remoteNavigation.remoteOverSquare] = {
                // add an inset border
                boxShadow: "inset 0 0 0 10px rgba(0,100,255,0.7)",
            };
        }

        // Highlight available moves when piece is selected/dragged.
        availableMoves.forEach((move) => {
            highlightStyles[move] =
                remoteNavigation.remoteOverSquare === move
                    ? {
                          background: `radial-gradient(circle, rgba(0, 255, 0, 0.5) 30%, transparent 50%)`,
                          borderRadius: "50%",
                      }
                    : {
                          background: `radial-gradient(circle, rgba(0, 0, 0, 0.58) 20%, #C7AE8E 27%, transparent 20%)`,
                          borderRadius: "50%",
                      };
            const div = document.querySelector(`[data-square="${move}"]`);
            div?.classList?.add("available-moves");
        });

        // Highlight puzzle hint square (source)
        if (gameState.puzzleHintSquare) {
            highlightStyles[gameState.puzzleHintSquare] = {
                background: "rgba(0, 150, 255, 0.6)",
                animation: "pulse-hint 1.5s ease-in-out infinite",
                zIndex: 10,
                position: "relative",
            };
        }

        // Highlight puzzle hint destination (target square for second hint)
        if (gameState.puzzleHintDestination) {
            highlightStyles[gameState.puzzleHintDestination] = {
                background: "rgba(0, 255, 150, 0.6)",
                animation: "pulse-hint 1.5s ease-in-out infinite",
                zIndex: 10,
                position: "relative",
            };
        }

        // Note: Incorrect move square highlighting is handled via CSS class in useEffect
        // This avoids applying styles to the entire square and allows the animation
        // to only affect the square background, not the piece

        return highlightStyles;
    }

    /**
     * Checks if a piece can be dragged.
     * Color must be the same as the player's color and it must be the player's turn.
     * The piece is of type string
     * @returns true if the piece can be dragged, false otherwise.
     */
    function isDraggablePiece({ piece }: { piece: ReactChessboardPiece }): boolean {
        if (!gameState.game) {
            console.error("Game is null.");
            return false;
        }
        const pieceColor = piece.charAt(0) as Color;
        return (
            !gameState.game.isGameOver() &&
            pieceColor === multiplayerState.playerColor &&
            gameState.game.turn() === multiplayerState.playerColor &&
            !multiplayerState.isHost &&
            !multiplayerState.forfeit.isForfeit
        );
    }
    const boardWidth = useResponsiveBoardWidth();

    // Handle retry after incorrect puzzle move
    const handleRetryPuzzle = () => {
        // Remove the CSS class from any square that has it
        const incorrectSquare = document.querySelector(".incorrect-square");
        if (incorrectSquare) {
            incorrectSquare.classList.remove("incorrect-square");
        }

        if (puzzleStateBeforeMove.current) {
            setGameState((prev) => ({
                ...prev,
                game: new Chess(puzzleStateBeforeMove.current!.fen),
                puzzleIndex: puzzleStateBeforeMove.current!.index,
                lastMove: null,
                puzzleHintSquare: null,
                puzzleHintDestination: null,
                puzzleHintLevel: 0,
            }));
        }
        setIncorrectMoveSquare(null);
        setShowIncorrectMoveModal(false);
    };

    return (
        <div
            className={`chessboard-wrapper orientation-${multiplayerState.playerColor === "w" || multiplayerState.isHost ? "white" : "black"}`}
            data-testid="custom-chessboard"
        >
            {showIncorrectMoveModal && <PuzzleIncorrectMove onRetry={handleRetryPuzzle} />}
            {showPuzzleCompleteModal && (
                <PuzzleComplete onClose={() => setShowPuzzleCompleteModal(false)} />
            )}
            <ChessboardDnDProvider>
                <Chessboard
                    id="chessboard"
                    data-testid="chessboard"
                    boardOrientation={`${multiplayerState.playerColor === "w" || multiplayerState.isHost ? "white" : "black"}`}
                    boardWidth={boardWidth}
                    autoPromoteToQueen={true}
                    areArrowsAllowed={false}
                    onMouseOutSquare={() => setHoveredSquare(null)}
                    onMouseOverSquare={(square) => setHoveredSquare(square)}
                    position={gameState.game?.fen()}
                    onPieceDrop={onDrop}
                    isDraggablePiece={isDraggablePiece}
                    onPieceDragBegin={onPieceDragBegin}
                    onPieceDragEnd={resetSelection}
                    onSquareClick={onSquareClick}
                    promotionDialogVariant="modal"
                    animationDuration={CustomBoardConstants.ANIMATION_DURATION}
                    customSquareStyles={customSquareStyles(isMobile)}
                    customDarkSquareStyle={{}} // Gets rid of default styles
                    customLightSquareStyle={{}}
                    customBoardStyle={{
                        transform: "perspective(1000px) rotateX(5deg)",
                        // filter: "brightness(1.2)",
                        boxShadow: "-30px -10px 8px 0px rgba(0, 0, 0, 0.5)",
                        transformOrigin: "center",
                        borderRadius: "10px",
                        backgroundImage: `url("/images/${
                            multiplayerState.playerColor === "b" && !multiplayerState.isHost
                                ? "chess-board-blk.webp"
                                : "chess-board-wht.webp"
                        }")`,
                        padding: calcBoardPadding(),
                        backgroundPosition: "center",
                        backgroundSize: "100% 100%",
                        backgroundRepeat: "no-repeat",
                        position: "relative",
                    }}
                    customPieces={useCustomPieces(
                        hoveredSquare,
                        clickedSquare,
                        onSquareClick,
                        remoteNavigation.remoteClickSquare,
                    )}
                    showBoardNotation={false}
                />
            </ChessboardDnDProvider>
        </div>
    );
}
